(function () {
  'use strict';

  class Carousel {
    constructor(root) {
      this.slides = Array.from(root.querySelectorAll('[data-quiz-slide]'));
      this.i = 0;
      if (this.slides.length > 1) this.timer = setInterval(() => this.next(), 5000);
    }
    next() {
      this.slides[this.i].classList.remove('is-active');
      this.i = (this.i + 1) % this.slides.length;
      this.slides[this.i].classList.add('is-active');
    }
    destroy() { if (this.timer) clearInterval(this.timer); }
  }

  class Quiz {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.quizId;
      this.keyAnswers = 'quiz:' + this.id + ':answers';
      this.keyPhoto = 'quiz:' + this.id + ':photo';
      this.$ = (s) => root.querySelector(s);
      this.$$ = (s) => Array.from(root.querySelectorAll(s));

      this.cfg = this.readConfig();
      this.current = 0;
      this.answers = [];
      this.stream = null;
      this.facefinder = null;
      this.detecting = false;
      this.picoReady = false;

      this.carousel = new Carousel(root);
      this.mountForm();

      // If the user already COMPLETED the quiz, restore their tailored results on refresh
      // (persists until they Retake or clear storage). Mid-quiz progress is never saved, so
      // an incomplete refresh always starts fresh.
      const saved = this.loadResults();
      if (saved && saved.completed && Array.isArray(saved.answers) && saved.answers.length === this.cfg.totalQuestions) {
        this.answers = saved.answers;
        this.bindRestart();
        this.showResults();
        return;
      }

      // Otherwise start completely fresh (also clears any stale/partial storage).
      this.clearSaved();
      this.answers = [];

      this.bind();
      this.$('[data-quiz-total]').textContent = this.cfg.totalQuestions;
      this.showQuestion();
      
      // Ensure pico and facefinder are properly initialized before camera usage
      if (this.cfg.camera) {
        this.facefinderReady = this.initPicoAndLoadFaceFinder();
      }
    }

    readConfig() {
      try { return JSON.parse(this.$('[data-quiz-config]').textContent); }
      catch (e) { console.error('[quiz] bad config', e); return { questions: [], totalQuestions: 0, camera: false }; }
    }
    // Remove any persisted quiz state. Called on a fresh start and on Retake.
    clearSaved() {
      try {
        localStorage.removeItem(this.keyAnswers);
        localStorage.removeItem(this.keyPhoto);
      } catch (e) { /* storage unavailable: nothing to clear */ }
    }
    // Persist a COMPLETED result set so a refresh keeps showing the tailored results
    // (until Retake or manual clear). Mid-quiz progress is intentionally never saved.
    saveResults() {
      try {
        localStorage.setItem(this.keyAnswers, JSON.stringify({ completed: true, answers: this.answers }));
      } catch (e) { /* storage unavailable */ }
    }
    loadResults() {
      try { return JSON.parse(localStorage.getItem(this.keyAnswers)); }
      catch (e) { return null; }
    }

    // The KYC form is rendered in an off-screen staging area ([data-quiz-kyc-source]) that is
    // visible to Shopify Forms' loader at page load (not display:none), so it actually mounts.
    // We only record whether a form exists here; the (already-mounted) form is moved into the
    // KYC step lazily by revealKycForm() when the user reaches it.
    mountForm() {
      const source = this.$('[data-quiz-kyc-source]');
      this.kycMoved = false;
      // Does a KYC form exist? If not, skip the step rather than show an empty card.
      this.hasKycForm = !!(source && (source.children.length > 0 || source.textContent.trim().length > 0));
      if (!this.hasKycForm) {
        console.warn('[quiz] no KYC form configured — skipping the email step. Add a form app block (or "Email form fallback" markup) to the Quiz section to enable it.');
      }
    }

    // Move the (by now mounted) form from the staging area into the visible KYC step. Moving
    // preserves the form's DOM nodes and their listeners; we do it once, on first KYC entry.
    revealKycForm() {
      if (this.kycMoved) return;
      const source = this.$('[data-quiz-kyc-source]');
      const mount = this.$('[data-quiz-kyc-mount]');
      if (source && mount) {
        while (source.firstChild) mount.appendChild(source.firstChild);
        this.kycMoved = true;
      }
    }

    bind() {
      this.$('[data-quiz-next]').addEventListener('click', () => this.next());
      this.$('[data-quiz-prev]').addEventListener('click', () => this.prev());
      this.$('[data-quiz-kyc-continue]').addEventListener('click', () => this.afterKyc());
      if (this.cfg.camera) {
        this.$('[data-quiz-camera-enable]').addEventListener('click', () => this.initCamera());
        this.$('[data-quiz-camera-skip]').addEventListener('click', () => this.analyze());
        this.$('[data-quiz-take-photo]').addEventListener('click', () => this.takePhoto());
        this.$('[data-quiz-photo-skip]').addEventListener('click', () => this.analyze());
        // Camera-error screen controls (markup only present when camera is enabled)
        const retry = this.$('[data-quiz-camera-retry]');
        if (retry) retry.addEventListener('click', () => this.initCamera());
        const errSkip = this.$('[data-quiz-error-skip]');
        if (errSkip) errSkip.addEventListener('click', () => this.analyze());
      }
      this.bindRestart();
    }
    bindRestart() {
      const b = this.$('[data-quiz-restart]');
      if (b) b.addEventListener('click', () => {
        this.clearSaved();
        window.location.reload();
      });
    }

    showScreen(name) {
      // On results, switch the section to its solid surface background (fades the hero out).
      this.root.classList.toggle('quiz--results', name === 'results');

      // results lives outside .quiz__shell, so collapse the shell (min-height:100vh) on results
      const shell = this.$('[data-quiz-shell]');
      if (shell) {
        shell.hidden = (name === 'results');
        shell.style.display = (name === 'results') ? 'none' : 'flex';
      }
      
      // Show all screens first (in case they're hidden from previous state)
      this.$$('[data-quiz-screen]').forEach((el) => {
        const isActive = el.dataset.quizScreen === name;
        el.hidden = !isActive;
        el.style.display = isActive ? 'block' : 'none';
      });
      
      // Results screen special handling
      const resultsScreen = this.$('[data-quiz-screen="results"]');
      if (resultsScreen) {
        resultsScreen.hidden = (name !== 'results');
        resultsScreen.style.display = (name === 'results') ? 'block' : 'none';
        if (name === 'results') {
          // Smoothly scroll the whole page to the top when results appear.
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }

    showQuestion() {
      const q = this.cfg.questions[this.current];
      this.$('[data-quiz-current]').textContent = this.current + 1;
      this.$('[data-quiz-question]').textContent = q.questionText;
      this.$('[data-quiz-prev]').disabled = this.current === 0;

      const saved = this.answers[this.current] || [];
      const wrap = this.$('[data-quiz-answers]');
      wrap.innerHTML = '';
      q.answers.forEach((ans, i) => {
        const id = this.id + '-q' + this.current + '-a' + i;
        const opt = document.createElement('div');
        opt.className = 'quiz__answer';
        const input = document.createElement('input');
        input.className = 'quiz__input';
        input.type = q.questionType;
        input.name = 'q' + this.id + '-' + this.current; // instance-scoped so radios don't cross-link
        input.id = id;
        input.value = ans;
        input.checked = saved.indexOf(ans) !== -1;
        input.addEventListener('change', () => { this.$('[data-quiz-next]').disabled = !this.$('.quiz__input:checked'); });
        const label = document.createElement('label');
        label.className = 'quiz__label';
        label.setAttribute('for', id);
        label.textContent = ans; // textContent avoids any markup-injection from answer text
        opt.appendChild(input);
        opt.appendChild(label);
        wrap.appendChild(opt);
      });
      this.$('[data-quiz-next]').disabled = !saved.length;
      this.showScreen('questions');
    }

    next() {
      this.answers[this.current] = this.$$('.quiz__input:checked').map((i) => i.value);
      if (this.current < this.cfg.totalQuestions - 1) { this.current++; this.showQuestion(); }
      else if (this.hasKycForm) { this.revealKycForm(); this.showScreen('kyc'); }
      else this.afterKyc(); // no form to collect: go straight to camera/analyze
    }
    prev() { if (this.current > 0) { this.current--; this.showQuestion(); } }

    afterKyc() { if (this.cfg.camera) this.showScreen('camera'); else this.analyze(); }

    analyze() {
      this.detecting = false;
      if (this.stream) { this.stream.getTracks().forEach((t) => t.stop()); this.stream = null; }
      this.showScreen('analyzing');
      setTimeout(() => this.showResults(), this.cfg.analyzeMs);
    }

    // Rank matched SKUs by how often the answers mapped to them, most-relevant first.
    // No cap — every matched product is returned; used only to order the results.
    processResults() {
      const counts = {};
      (this.answers || []).forEach((selected, qi) => {
        const q = this.cfg.questions[qi];
        if (!q) return;
        (selected || []).forEach((ans) => {
          (q.mapping[ans] || []).forEach((sku) => { counts[sku] = (counts[sku] || 0) + 1; });
        });
      });
      return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    }

    showResults() {
      // Persist the completed result set so a refresh keeps showing these tailored results.
      this.saveResults();

      const grid = this.$('[data-quiz-grid]');
      // Every product the answers map to (from the settings mapping), most-relevant first, no cap.
      const matched = this.processResults();

      if (matched.length) {
        matched.forEach((sku) => {
          const card = this.$('[data-quiz-product][data-card-id="' + sku + '"]');
          if (card) {
            card.hidden = false;
            card.style.removeProperty('display');
            if (grid) grid.appendChild(card); // keep matched in relevance order
          }
        });
        console.log('[quiz] results: showing ' + matched.length + ' mapped products');
      } else {
        // No mapping matched the answers — show all products so results aren't empty.
        this.$$('[data-quiz-product]').forEach((card) => {
          card.hidden = false;
          card.style.removeProperty('display');
        });
        console.warn('[quiz] no mapped matches — showing all products as fallback');
      }

      // Ensure restart button is visible and functional
      const restartBtn = this.$('[data-quiz-restart]');
      if (restartBtn) {
        restartBtn.hidden = false;
        restartBtn.style.removeProperty('display');
        restartBtn.disabled = false;
      }

      this.showScreen('results');
      console.log('[quiz] results screen displayed');

      // Attach the "Quiz Result" line-item property to every Add-to-cart form in the results.
      this.tagCartFormsWithQuizResult();
    }

    // The note value attached to cart items — the same fixed tag on every quiz-added product.
    quizResultLabel() {
      return 'added by quiz';
    }

    // Inject name="properties[Quiz Result]" into each /cart/add form so the note is captured
    // on submission. Covers inline quick-add forms now + quick-add modal forms added later.
    tagCartFormsWithQuizResult() {
      const value = this.quizResultLabel();
      const inject = (form) => {
        if (!form || form.dataset.quizTagged || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
        form.dataset.quizTagged = '1';
        const input = document.createElement('input');
        input.type = 'hidden';                       // ← change to 'text' for an editable note
        // Underscore prefix = the property is saved on the order (merchant sees it) but is
        // HIDDEN from the customer in cart/checkout. Drop the "_" to show it to the customer.
        input.name = 'properties[_Quiz Result]';
        input.value = value;
        form.appendChild(input);
      };
      document.querySelectorAll('form[action*="/cart/add"]').forEach(inject);
      // Quick-add modals inject their form later — watch for it.
      if (!this.formObserver) {
        this.formObserver = new MutationObserver((muts) => {
          muts.forEach((m) => m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches('form[action*="/cart/add"]')) inject(node);
            if (node.querySelectorAll) node.querySelectorAll('form[action*="/cart/add"]').forEach(inject);
          }));
        });
        this.formObserver.observe(document.body, { childList: true, subtree: true });
      }
    }

    // ---- Camera + on-device face detection (uses the global `pico` from quiz-pico.js) ----
    
    // Initialize Pico.js and load the face detection cascade file
    async initPicoAndLoadFaceFinder() {
      // Check if pico is available from the script tag
      if (typeof pico === 'undefined') {
        console.warn('[quiz] pico.js not loaded, face detection unavailable');
        this.picoReady = false;
        return;
      }

      const url = this.cfg.facefinderUrl;
      if (!url) {
        console.warn('[quiz] facefinder URL not configured');
        this.picoReady = false;
        return;
      }

      // First try the configured (versioned) URL. If the bytes don't pass validation —
      // which happens while Shopify's edge cache still hands out an older, possibly
      // corrupt asset version under the same ?v= hash — refetch the version-stripped
      // canonical URL, which always resolves to the latest upload.
      const cascade =
        (await this.tryLoadCascade(url)) ||
        (url.indexOf('?') !== -1 ? await this.tryLoadCascade(url.split('?')[0], true) : null);

      if (!cascade) {
        this.picoReady = false;
        return;
      }
      this.facefinder = cascade;
      this.picoReady = true;
      console.log('[quiz] facefinder loaded and ready');
    }

    // Read the picojs cascade header (skip 8 bytes, then little-endian int32 tdepth and
    // int32 ntrees) and sanity-check it. A UTF-8-corrupted file yields absurd values that
    // would otherwise make unpack_cascade allocate enormous arrays and throw a RangeError
    // ("Maximum call stack size exceeded"). The genuine facefinder is tdepth=6, ntrees=468.
    parseCascadeHeader(bytes) {
      if (bytes.length < 16) return { valid: false, reason: 'too short' };
      const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const tdepth = dv.getInt32(8, true);
      const ntrees = dv.getInt32(12, true);
      const valid = tdepth >= 1 && tdepth <= 12 && ntrees >= 1 && ntrees <= 8192;
      // The on-disk size is exactly 16 + ntrees * 8 * 2^tdepth; verify when the header is sane.
      const expectedSize = valid ? 16 + ntrees * 8 * Math.pow(2, tdepth) : 0;
      return { valid, tdepth, ntrees, expectedSize };
    }

    // Fetch + validate + unpack a single cascade URL. Returns the classify function, or
    // null on any problem (bad status, empty/HTML/corrupt body) so the caller can retry.
    async tryLoadCascade(url, isRetry) {
      try {
        console.log('[quiz] fetching facefinder from:', url, isRetry ? '(retry: version-stripped)' : '');
        const res = await fetch(url, isRetry ? { cache: 'reload' } : undefined);
        if (!res.ok) {
          console.error('[quiz] facefinder fetch failed:', res.status, res.statusText);
          return null;
        }

        const buf = await res.arrayBuffer();
        if (!buf || buf.byteLength === 0) {
          console.error('[quiz] facefinder buffer is empty or null');
          return null;
        }

        const bytes = new Uint8Array(buf);
        // Response is an HTML error page (starts with '<' or '!') rather than binary?
        if (bytes[0] === 60 || bytes[0] === 33) {
          console.error('[quiz] facefinder returned HTML instead of binary (likely 404 page), size:', buf.byteLength);
          return null;
        }

        // Validate the header BEFORE unpacking so corrupt/stale bytes can't blow the stack.
        const hdr = this.parseCascadeHeader(bytes);
        if (!hdr.valid || (hdr.expectedSize && hdr.expectedSize !== buf.byteLength)) {
          console.error('[quiz] facefinder header invalid — corrupt or stale asset version. size:', buf.byteLength, 'header:', hdr);
          return null;
        }

        console.log('[quiz] facefinder buffer loaded, size:', buf.byteLength, 'bytes (tdepth=' + hdr.tdepth + ', ntrees=' + hdr.ntrees + ')');

        const cascade = pico.unpack_cascade(new Int8Array(buf));
        if (typeof cascade !== 'function') {
          console.error('[quiz] unpack_cascade did not return a function');
          return null;
        }
        return cascade;
      } catch (e) {
        console.error('[quiz] facefinder load error:', e.name, e.message);
        return null;
      }
    }

    rgbaToGray(rgba, nrows, ncols) {
      const g = new Uint8Array(nrows * ncols);
      for (let r = 0; r < nrows; r++) {
        for (let c = 0; c < ncols; c++) {
          g[r * ncols + c] = (2 * rgba[r * 4 * ncols + 4 * c] + 7 * rgba[r * 4 * ncols + 4 * c + 1] + rgba[r * 4 * ncols + 4 * c + 2]) / 10;
        }
      }
      return g;
    }

    // Reject with a TimeoutError if `promise` doesn't settle within `ms` ms.
    withTimeout(promise, ms) {
      return new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(Object.assign(new Error('timed out'), { name: 'TimeoutError' })), ms);
        promise.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
      });
    }

    async initCamera() {
      // getUserMedia normally resolves/rejects fast, but some camera stacks (e.g. IPU6 on
      // Linux/Chrome) hang forever — so we cap it and stop any late-arriving stream.
      const gum = navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      try {
        this.stream = await this.withTimeout(gum, 12000);
      } catch (e) {
        console.error('[quiz] camera unavailable:', e.name, e.message);
        gum.then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {}); // stop a stream that arrives after the timeout
        this.showCameraError(e);
        return;
      }

      this.showScreen('camera-view');
      const video = this.$('[data-quiz-video]');
      const wrap = this.$('[data-quiz-video-wrap]');
      video.srcObject = this.stream;

      // Wait for the camera to actually deliver frame dimensions. A connected-but-dead
      // camera never fires loadedmetadata, so cap the wait and fail to the error screen.
      try {
        if (video.readyState < 1) {
          await this.withTimeout(new Promise((r) => video.addEventListener('loadedmetadata', r, { once: true })), 8000);
        }
        await video.play();
      } catch (e) {
        console.error('[quiz] camera stream did not start:', e.name, e.message);
        if (this.stream) { this.stream.getTracks().forEach((t) => t.stop()); this.stream = null; }
        this.showCameraError(e);
        return;
      }

      this.detCanvas = document.createElement('canvas');
      this.detCanvas.width = 640;
      this.detCanvas.height = 480;
      this.detCtx = this.detCanvas.getContext('2d', { willReadFrequently: true });

      // overlay circle, scoped to THIS instance's video wrapper (no global ids)
      this.circle = wrap.querySelector('.quiz__face') || document.createElement('div');
      this.circle.className = 'quiz__face';
      wrap.appendChild(this.circle);

      // Wait for facefinder to be ready before starting detection
      if (this.facefinderReady) {
        await this.facefinderReady;
      }

      this.runDetection(video, wrap);
    }

    // Surface a getUserMedia failure on the dedicated camera-error screen (with a message
    // tailored to the error) instead of silently skipping. Falls back to analyze() if the
    // theme markup doesn't include that screen.
    showCameraError(err) {
      const screen = this.$('[data-quiz-screen="camera-error"]');
      if (!screen) { this.analyze(); return; }
      const msgEl = this.$('[data-quiz-error-message]');
      if (msgEl) msgEl.textContent = this.cameraErrorMessage(err);
      this.showScreen('camera-error');
    }

    cameraErrorMessage(err) {
      switch (err && err.name) {
        case 'NotAllowedError':
        case 'SecurityError':
          return 'Camera access was blocked. Allow the camera in your browser, then retry — or continue without it.';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
          return 'No camera was found on this device. You can continue without a photo.';
        case 'NotReadableError':
        case 'TrackStartError':
          return 'Your camera is already in use by another app. Close it and retry, or continue without a photo.';
        case 'TimeoutError':
          return 'Your camera didn\'t respond. Retry, or continue without a photo.';
        default:
          return 'We couldn\'t access your camera. Retry, or continue without a photo.';
      }
    }

    runDetection(video, wrap) {
      const takeBtn = this.$('[data-quiz-take-photo]');
      
      // Check if pico and facefinder are available
      if (!this.picoReady || !this.facefinder || typeof pico === 'undefined') {
        console.warn('[quiz] pico detection unavailable, allowing photo unconditionally');
        if (takeBtn) takeBtn.disabled = false; // no detector available: allow photo unconditionally
        return;
      }

      const mem = pico.instantiate_detection_memory(5);
      const params = { 
        shiftfactor: 0.1, 
        minsize: 100, 
        maxsize: 1000, 
        scalefactor: 1.1 
      };

      this.detecting = true;
      const loop = () => {
        if (!this.detecting) return;
        
        const w = wrap.offsetWidth;
        const h = wrap.offsetHeight;
        
        if (w && h && video.readyState === video.HAVE_ENOUGH_DATA) {
          try {
            this.detCtx.drawImage(video, 0, 0, 640, 480);
            const rgba = this.detCtx.getImageData(0, 0, 640, 480).data;
            
            const image = { 
              pixels: this.rgbaToGray(rgba, 480, 640), 
              nrows: 480, 
              ncols: 640, 
              ldim: 640 
            };

            let dets = pico.run_cascade(image, this.facefinder, params);
            dets = mem(dets);
            dets = pico.cluster_detections(dets, 0.2);

            const sx = w / 640;
            const sy = h / 480;
            let found = false;

            for (let i = 0; i < dets.length; i++) {
              // pico's empirical quality threshold; lower = more false positives
              if (dets[i][3] > 50.0) {
                const y = dets[i][0];
                const x = dets[i][1];
                const ss = dets[i][2] * Math.min(sx, sy);
                
                this.circle.style.left = (w - x * sx - ss / 2) + 'px';
                this.circle.style.top = (y * sy - ss / 2) + 'px';
                this.circle.style.width = ss + 'px';
                this.circle.style.height = ss + 'px';
                this.circle.style.display = 'block';
                found = true;
                break;
              }
            }

            if (!found) {
              this.circle.style.display = 'none';
            }

            if (takeBtn) {
              takeBtn.disabled = !found;
            }
          } catch (e) {
            console.error('[quiz] detection loop error', e);
            if (takeBtn) takeBtn.disabled = false;
          }
        }

        requestAnimationFrame(loop);
      };

      requestAnimationFrame(loop);
    }

    async takePhoto() {
      this.detecting = false;
      const video = this.$('[data-quiz-video]');
      const canvas = this.$('[data-quiz-canvas]');
      const ctx = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      ctx.scale(-1, 1);
      ctx.translate(-canvas.width, 0);
      ctx.drawImage(video, 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      // Photo is used only for the on-screen capture; it is never persisted (no saved data).
      this.analyze();
    }

    // Stop timers/camera so theme-editor reloads don't leak intervals or live streams.
    destroy() {
      this.detecting = false;
      if (this.carousel) this.carousel.destroy();
      if (this.formObserver) { this.formObserver.disconnect(); this.formObserver = null; }
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
    }
  }

  const instances = {};

  function init(scope) {
    (scope || document).querySelectorAll('[data-quiz-root]').forEach((root) => {
      if (root.dataset.quizReady) return;
      root.dataset.quizReady = '1';
      try { 
        instances[root.dataset.quizId] = new Quiz(root); 
      }
      catch (e) { 
        console.error('[quiz] init failed', e); 
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor lifecycle: re-init on add/reload, clean up on remove.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const inst = instances[e.detail.sectionId];
    if (inst) {
      inst.destroy();
      delete instances[e.detail.sectionId];
    }
  });

  // Page reload / navigation away mid-quiz: stop any live camera streams and timers.
  // (Browsers eventually release getUserMedia on unload, but this kills the camera
  // light immediately and prevents a stale stream surviving into the bfcache.)
  window.addEventListener('pagehide', () => {
    Object.keys(instances).forEach((id) => { if (instances[id]) instances[id].destroy(); });
  });
})();
